import { ProductCartItemAnalyticsService, ProductListAnalyticsService } from '@analytics/feature';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  Optional,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ProductAvailabilityFacade } from '@availability/data-access';
import { CustomOrderEntry } from '@cart/const';
import { CustomActiveCartService } from '@cart/data-access';
import { CustomProduct, SelectedPrice } from '@product/const';
import { SelectedPriceService } from '@product/data-access';
import { convertFromSaleUnit, convertToSaleUnit, ProductPipesModule } from '@product/utils';
import { SdvorButtonComponent } from '@sdvor/shared/ui/v2';
import { combineLatest, filter, map, Observable, switchMap, take } from 'rxjs';
import { ProductItemCounterComponent } from '../product-item-counter';
import { ItemAddToCartComponent } from './add-to-cart';

@Component({
  selector: 'sd-product-button',
  templateUrl: './product-button.component.html',
  styleUrls: ['./product-button.component.scss'],
  imports: [
    AsyncPipe,
    ProductItemCounterComponent,
    ProductPipesModule,
    ReactiveFormsModule,
    SdvorButtonComponent,
    ItemAddToCartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductButtonComponent implements OnInit, OnChanges {
  @Input() product: CustomProduct;
  @Input() productIndex?: number;

  quantityControl: FormControl;

  loading$: Observable<boolean>;
  cartEntry$: Observable<CustomOrderEntry | undefined>;
  productPrice$: Observable<SelectedPrice>;
  maxQuantity$: Observable<number>;

  constructor(
    private customActiveCartService: CustomActiveCartService,
    private productAvailabilityFacade: ProductAvailabilityFacade,
    private selectedPriceService: SelectedPriceService,
    private destroyRef: DestroyRef,
    private productCartItemAnalyticsService: ProductCartItemAnalyticsService,
    @Optional() private productListAnalyticsService: ProductListAnalyticsService,
  ) {
    this.quantityControl = new FormControl(this.product?.price?.orderMinimum || 1);
  }

  ngOnInit(): void {
    this.initializeProductData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.product) {
      if (this.quantityControl) {
        this.quantityControl.setValue(this.product.price?.orderMinimum || 1, { emitEvent: false });
      }

      this.initializeProductData();
    }
  }

  protected saveProductAnalytics(): void {
    this.productCartItemAnalyticsService.saveForProduct(this.product, {
      listName: this.productListAnalyticsService?.getListName(),
      productIndex: this.resolveProductIndex(),
    });
  }

  private resolveProductIndex(): number | undefined {
    if (this.productIndex !== undefined && this.productIndex !== null) {
      return this.productIndex;
    }

    return this.productListAnalyticsService?.getProductIndex(this.product.code);
  }

  private initializeProductData(): void {
    this.cartEntry$ = this.customActiveCartService.getEntry(this.product.code);
    this.productPrice$ = this.selectedPriceService.getSelectedPrice(this.product.code);
    this.setupQuantityControl();
    this.setupInitialQuantity();
    this.setupLoading();
    this.setupMaxQuantity();
  }

  private setupQuantityControl(): void {
    combineLatest({ selectedPrice: this.productPrice$, quantity: this.quantityControl.valueChanges })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ selectedPrice, quantity }) => {
        this.customActiveCartService
          .getEntry(this.product.code)
          .pipe(take(1), filter(Boolean))
          .subscribe((entry: CustomOrderEntry) => {
            if (entry.positionGuid) {
              this.saveProductAnalytics();
              this.customActiveCartService.debouncedUpdateEntry(
                entry.positionGuid,
                convertToSaleUnit(selectedPrice, quantity).amount,
              );
            }
          });
      });
  }

  private setupInitialQuantity(): void {
    combineLatest({ selectedPrice: this.productPrice$, entry: this.cartEntry$ })
      .pipe(
        filter(({ entry }) => Boolean(entry)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ selectedPrice, entry }) => {
        const value = convertFromSaleUnit(selectedPrice, entry.quantity).amount;
        this.quantityControl.setValue(value, { emitEvent: false });
      });
  }

  private setupLoading(): void {
    this.loading$ = combineLatest([
      this.productAvailabilityFacade.getAvailabilityLoading(this.product.code),
      this.customActiveCartService.loading$,
    ]).pipe(map((allLoaders) => allLoaders.some((loadingState) => loadingState === true)));

    this.loading$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((loading: boolean) => {
      const disableState = loading ? 'disable' : 'enable';
      this.quantityControl[disableState]({ emitEvent: false });
    });
  }

  private setupMaxQuantity(): void {
    this.maxQuantity$ = this.productPrice$.pipe(
      switchMap((selectedPrice: SelectedPrice) =>
        this.customActiveCartService
          .getMaxQuantityByEntry(this.product.code)
          .pipe(map((value) => value && convertFromSaleUnit(selectedPrice, value).amount)),
      ),
    );
  }
}
